// A-Regime — ADX chop/trend filter. Zero AI. Zero guessing.
// ═══════════════════════════════════════════════════════════════════════════
// NEW agent — did not exist before. Direct port of the ADX filter added to
// JarvisSMC_Strategy.pine after BTCUSD 4h hit an 11/11 loss streak during a
// ranging period. This is the single most important filter for avoiding
// that specific failure mode: it blocks entries when the market has no
// clear directional trend, regardless of what Structure/Fibonacci say.
//
// ⚠️ Even WITH this filter, a later BTCUSD 4h backtest still went 0/6 — so
// that specific instrument+timeframe combo stays EXCLUDED outright
// (jarvisDNA.js → trading.validated_strategy.excluded_combos), this filter
// alone is not a full fix for it.
// ═══════════════════════════════════════════════════════════════════════════

import { JARVIS_DNA } from '../../dna/jarvisDNA';

// Wilder's smoothed sum: out[i] = out[i-1] - out[i-1]/period + values[i]
// (classic ADX-paper style; used here for the TR/+DM/-DM stage — the ratio
// +DM_smooth/TR_smooth this feeds into is scale-invariant, so this "sum"
// style and an RMA "average" style give the same +DI/-DI result).
function wilderSum(values, period, startIdx) {
  const out = new Array(values.length).fill(null);
  let sum = 0;
  for (let i = startIdx; i < startIdx + period && i < values.length; i++) sum += values[i];
  out[startIdx + period - 1] = sum;
  for (let i = startIdx + period; i < values.length; i++) {
    out[i] = out[i - 1] - (out[i - 1] / period) + values[i];
  }
  return out;
}

// candles: [{ open, high, low, close, date }, ...] oldest → newest
export async function run({ candles = [], adxLen, adxSmooth, threshold }) {
  // Read from JARVIS_DNA here, not at module top-level — see A_Fibonacci.js
  // for why (crashed the whole app in production build otherwise).
  const { regime_filter } = JARVIS_DNA.validated_strategy;
  const len      = adxLen    ?? regime_filter.length;
  const smoothLn = adxSmooth ?? regime_filter.smoothing;
  const thresh   = threshold ?? regime_filter.threshold;

  const need = len + smoothLn + 2;
  if (!candles || candles.length < need) {
    return { ok: false, error: `Need at least ${need} candles for ADX (got ${candles?.length || 0})`, agent: 'A-Regime', requiresAI: false, ts: Date.now() };
  }

  const c = candles;
  const n = c.length;

  const tr      = new Array(n).fill(0);
  const plusDM  = new Array(n).fill(0);
  const minusDM = new Array(n).fill(0);

  for (let i = 1; i < n; i++) {
    const upMove   = c[i].high - c[i - 1].high;
    const downMove = c[i - 1].low - c[i].low;
    plusDM[i]  = (upMove > downMove && upMove > 0) ? upMove : 0;
    minusDM[i] = (downMove > upMove && downMove > 0) ? downMove : 0;

    const highLow   = c[i].high - c[i].low;
    const highClose = Math.abs(c[i].high - c[i - 1].close);
    const lowClose  = Math.abs(c[i].low  - c[i - 1].close);
    tr[i] = Math.max(highLow, highClose, lowClose);
  }

  const smoothTR      = wilderSum(tr, len, 1);
  const smoothPlusDM  = wilderSum(plusDM, len, 1);
  const smoothMinusDM = wilderSum(minusDM, len, 1);

  const plusDI  = new Array(n).fill(null);
  const minusDI = new Array(n).fill(null);
  const dx      = new Array(n).fill(null);

  for (let i = 0; i < n; i++) {
    if (smoothTR[i] != null && smoothTR[i] !== 0) {
      plusDI[i]  = (smoothPlusDM[i]  / smoothTR[i]) * 100;
      minusDI[i] = (smoothMinusDM[i] / smoothTR[i]) * 100;
      const sum = plusDI[i] + minusDI[i];
      dx[i] = sum !== 0 ? (Math.abs(plusDI[i] - minusDI[i]) / sum) * 100 : 0;
    }
  }

  const firstDxIdx = dx.findIndex(v => v !== null);
  if (firstDxIdx === -1 || n - firstDxIdx < smoothLn) {
    return { ok: false, error: 'Not enough data to smooth ADX yet', agent: 'A-Regime', requiresAI: false, ts: Date.now() };
  }

  // ADX = RMA of DX over `smoothLn` — average-style smoothing (matches
  // ta.dmi's ADX stage), seeded with a simple average.
  let sum = 0;
  for (let i = firstDxIdx; i < firstDxIdx + smoothLn; i++) sum += dx[i];
  let adxVal = sum / smoothLn;
  for (let i = firstDxIdx + smoothLn; i < n; i++) {
    adxVal = (adxVal * (smoothLn - 1) + dx[i]) / smoothLn;
  }

  const isTrending = adxVal > thresh;

  return {
    ok:  true,
    adx: +adxVal.toFixed(1),
    plusDI:  plusDI[n - 1]  != null ? +plusDI[n - 1].toFixed(1)  : null,
    minusDI: minusDI[n - 1] != null ? +minusDI[n - 1].toFixed(1) : null,
    threshold: thresh,
    isTrending,
    regime: isTrending ? 'TRENDING' : 'CHOPPY',
    recommendation: isTrending
      ? `ADX ${adxVal.toFixed(1)} > ${thresh} — trending, trade घेता येईल.`
      : `ADX ${adxVal.toFixed(1)} ≤ ${thresh} — choppy market, ट्रेड SKIP करा.`,
    agent:      'A-Regime',
    requiresAI: false,
    ts:         Date.now(),
  };
}
